import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Eye, EyeOff, Loader2, LogIn, Clock, XCircle, ArrowLeft, KeyRound, ArrowRight, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const heading = { fontFamily: "'Plus Jakarta Sans', 'Manrope', sans-serif" };

export default function UnifiedLogin() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ identifier: '', password: '' });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingMsg, setPendingMsg] = useState('');

  const [forgotOpen, setForgotOpen] = useState(false);
  const [resetStep, setResetStep] = useState('request');
  const [resetUsername, setResetUsername] = useState('');
  const [resetCode, setResetCode] = useState(['', '', '', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const codeRefs = useRef([]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setPendingMsg('');
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/unified/login`, form);
      localStorage.setItem('unified_token', data.token);
      localStorage.setItem('token', data.token);
      localStorage.setItem('einvoice_token', data.token);
      localStorage.setItem('unified_user', JSON.stringify(data.user));
      const userWithToken = { ...data.user, token: data.token };
      localStorage.setItem('user', JSON.stringify(userWithToken));

      if (data.is_pending) {
        window.location.href = '/auth/pending';
        return;
      }
      if (data.redirect === 'pms') {
        window.location.href = '/dashboard';
      } else if (data.redirect === 'einvoice') {
        window.location.href = '/einvoice/portal/dashboard';
      } else if (data.redirect === 'hr') {
        window.location.href = '/hr/portal/applicants';
      } else {
        // E-Ticket sub-users have no standard module — route them to their workspace
        const mods = data.user?.modules || {};
        const hasStd = ['pms', 'einvoice', 'hr'].some(m => mods[m]?.enabled);
        if (!hasStd) {
          try {
            const me = await axios.get(`${API_URL}/api/eticket/company/me`, { headers: { Authorization: `Bearer ${data.token}` } });
            if (me.data.company) { window.location.href = '/eticket/admin'; return; }
          } catch (e) { /* fall through */ }
        }
        window.location.href = '/module-selector';
      }
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || '';
      if (status === 403) {
        setPendingMsg(detail);
      } else {
        setError(detail || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const openForgot = () => {
    setForgotOpen(true);
    setResetStep('request');
    setResetUsername(form.identifier);
    setResetCode(['', '', '', '', '', '']);
    setNewPassword('');
    setConfirmPassword('');
    setMaskedEmail('');
  };

  const closeForgot = () => {
    setForgotOpen(false);
    setResetStep('request');
    setResetCode(['', '', '', '', '', '']);
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    if (!resetUsername.trim()) { toast.error('Please enter your username'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/unified/forgot-password`, { username: resetUsername });
      if (res.data.success) {
        setMaskedEmail(res.data.masked_email || '***');
        setResetStep('verify');
        toast.success('Reset code sent to your email');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    const code = resetCode.join('');
    if (code.length !== 6) { toast.error('Enter the 6-digit code'); return; }
    if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/unified/reset-password`, {
        username: resetUsername, code, new_password: newPassword,
      });
      if (res.data.success) {
        setResetStep('done');
        toast.success('Password reset successfully!');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeInput = (index, value) => {
    if (value.length > 1) value = value[0];
    const updated = [...resetCode];
    updated[index] = value;
    setResetCode(updated);
    if (value && index < 5) codeRefs.current[index + 1]?.focus();
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !resetCode[index] && index > 0) {
      codeRefs.current[index - 1]?.focus();
    }
  };

  /* ─── PENDING / REJECTED STATE ─── */
  if (pendingMsg) {
    const isRejected = pendingMsg.toLowerCase().includes('declined');
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <LandingNavBar />
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md text-center">
            <div className={`mx-auto w-16 h-16 ${isRejected ? 'bg-red-50' : 'bg-amber-50'} rounded-2xl flex items-center justify-center mb-6`}>
              {isRejected ? <XCircle className="w-8 h-8 text-red-500" /> : <Clock className="w-8 h-8 text-amber-500" />}
            </div>
            <h2 className="text-2xl font-semibold text-slate-900 mb-2" style={heading} data-testid="pending-card">
              {isRejected ? 'Account Declined' : 'Pending Approval'}
            </h2>
            <p className="text-slate-500 mb-8">{pendingMsg}</p>
            <button onClick={() => setPendingMsg('')} className="inline-flex items-center gap-2 text-sm text-[#2A5C4A] hover:text-[#1D4034] font-medium" data-testid="back-to-login">
              <ArrowLeft className="w-4 h-4" /> Back to login
            </button>
          </div>
        </div>
        <LandingFooter />
      </div>
    );
  }

  /* ─── MAIN LOGIN ─── */
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <LandingNavBar />

      <div className="flex-1 flex flex-col lg:flex-row pt-20">
        {/* ── LEFT: FORM ── */}
        <div className="flex-1 flex items-center justify-center p-6 lg:p-12 xl:p-24">
          <div className="w-full max-w-md">
            {/* Branding */}
            <div className="mb-10">
              <p className="text-xs font-semibold text-[#2A5C4A] tracking-[0.15em] uppercase mb-3">Welcome back</p>
              <h1 className="text-4xl sm:text-5xl font-light tracking-tighter text-slate-900 mb-2" style={heading}>
                Sign in to<br />your account
              </h1>
              <p className="text-slate-500 text-base leading-relaxed">
                Sign in to your StayMii account — <strong className="text-emerald-700">HR</strong> (free applicant tracking), <strong className="text-slate-700">Lite</strong> (hotel PMS), or <strong className="text-slate-700">e-Invoice</strong> (LHDN MyInvois automation).
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-card">
              {error && (
                <Alert variant="destructive" className="bg-red-50 border-red-200 rounded-xl">
                  <AlertDescription className="text-red-600 text-sm">{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label className="text-xs font-semibold text-slate-500 tracking-[0.05em] uppercase">Username or Email</Label>
                <Input
                  value={form.identifier}
                  onChange={e => setForm({ ...form, identifier: e.target.value })}
                  required
                  autoFocus
                  placeholder="Enter your username or email"
                  className="h-14 rounded-xl bg-[#F9F9F8] border-slate-200 text-slate-900 text-base placeholder:text-slate-400 focus:border-[#2A5C4A] focus:ring-1 focus:ring-[#2A5C4A]"
                  data-testid="login-identifier"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold text-slate-500 tracking-[0.05em] uppercase">Password</Label>
                <div className="relative">
                  <Input
                    type={showPw ? 'text' : 'password'}
                    value={form.password}
                    onChange={e => setForm({ ...form, password: e.target.value })}
                    required
                    placeholder="Enter your password"
                    className="h-14 rounded-xl bg-[#F9F9F8] border-slate-200 text-slate-900 text-base placeholder:text-slate-400 pr-12 focus:border-[#2A5C4A] focus:ring-1 focus:ring-[#2A5C4A]"
                    data-testid="login-password"
                  />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                    {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={openForgot}
                    className="text-[#2A5C4A] hover:text-[#1D4034] text-sm font-medium transition-colors"
                    data-testid="forgot-password-link"
                  >
                    Forgot Password?
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-14 rounded-xl bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-base font-semibold transition-all"
                data-testid="login-submit"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    Sign In <ArrowRight className="w-4 h-4" />
                  </span>
                )}
              </Button>

              <p className="text-center text-slate-500 text-sm pt-2">
                Don't have an account?{' '}
                <Link to="/auth/signup" className="text-[#2A5C4A] hover:text-[#1D4034] font-semibold transition-colors" data-testid="goto-signup">
                  Sign up
                </Link>
              </p>

              {/* Free HR fast-path */}
              <Link
                to="/auth/signup?category=hr"
                className="mt-2 flex items-center justify-between gap-3 p-4 rounded-xl border-2 border-emerald-200 bg-emerald-50 hover:bg-emerald-100 hover:border-emerald-300 transition-all group"
                data-testid="login-hr-fastpath"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-emerald-600 text-white flex items-center justify-center shrink-0">
                    <Sparkles className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-emerald-900 leading-tight">New? Get free StayMii HR in 30 seconds</p>
                    <p className="text-xs text-emerald-700 mt-0.5">Free public job application form + applicant tracking. Auto-approved.</p>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-emerald-700 group-hover:translate-x-0.5 transition-transform shrink-0" />
              </Link>
            </form>
          </div>
        </div>

        {/* ── RIGHT: IMAGE ── */}
        <div className="hidden lg:block lg:w-1/2 relative">
          <img
            src="https://customer-assets.emergentagent.com/job_5e74271a-8fbb-4f1f-8b01-d4ef0a762567/artifacts/6f5dk9vv_dJVESKEsB0OrtZzUcbl8LK675lrxdVwnJKgiXWHcXQC0e2AkLwSxHV44fcwmpbuslbI09-WWfncqHqJfh9oyDjg6qLTvoqL2EstzaV4FoQEL6RQcx-oN8Xca1Gz4ZvQdfdAmtDlrB9B7QgZQSYdGrCyiy4jKsYDTtDGWOTdsROEJZzgrVRo4fubJ3bfSTbC4.jpg"
            alt="Hotel receptionist welcoming guest"
            className="absolute inset-0 w-full h-full object-cover"
          />
          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          {/* Caption */}
          <div className="absolute bottom-0 left-0 right-0 p-10">
            <p className="text-white/60 text-xs font-semibold tracking-[0.15em] uppercase mb-3">StayMii Platform</p>
            <h2 className="text-3xl font-light text-white tracking-tight leading-snug" style={heading}>
              One login. Three<br />StayMii modules.
            </h2>
            <p className="text-white/75 text-sm mt-3 max-w-sm leading-relaxed">
              Run hospitality compliance, channel management, e-invoicing, and free
              HR recruitment from a single secure StayMii account.
            </p>
            <p className="text-emerald-300 text-xs mt-3">New here? StayMii HR is 100% free for Malaysian SMEs.</p>
          </div>
        </div>
      </div>

      <LandingFooter />

      {/* ─── FORGOT PASSWORD MODAL ─── */}
      {forgotOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="forgot-password-modal">
          <div className="bg-white rounded-3xl p-8 sm:p-10 w-full max-w-md shadow-2xl shadow-black/5">
            {resetStep === 'request' && (
              <form onSubmit={handleSendCode}>
                <h3 className="text-2xl font-semibold text-slate-900 mb-2" style={heading}>Forgot Password</h3>
                <p className="text-slate-500 text-sm mb-8">Enter your username and we'll send a reset code to your email.</p>
                <Label className="text-xs font-semibold text-slate-500 tracking-[0.05em] uppercase">Username</Label>
                <Input
                  value={resetUsername}
                  onChange={e => setResetUsername(e.target.value)}
                  className="mt-2 mb-8 h-14 rounded-xl bg-[#F9F9F8] border-slate-200 text-slate-900 focus:border-[#2A5C4A] focus:ring-1 focus:ring-[#2A5C4A]"
                  data-testid="reset-username-input"
                  autoFocus
                />
                <div className="flex gap-3">
                  <Button type="button" variant="outline" onClick={closeForgot} className="flex-1 h-12 rounded-xl border-slate-200 text-slate-600 hover:bg-slate-50" data-testid="cancel-reset-btn">
                    Cancel
                  </Button>
                  <Button type="submit" disabled={loading} className="flex-1 h-12 rounded-xl bg-[#2A5C4A] hover:bg-[#1D4034] text-white" data-testid="send-reset-code-btn">
                    {loading ? 'Sending...' : 'Send Reset Code'}
                  </Button>
                </div>
              </form>
            )}

            {resetStep === 'verify' && (
              <form onSubmit={handleResetPassword}>
                <h3 className="text-2xl font-semibold text-slate-900 mb-2" style={heading}>Reset Password</h3>
                <p className="text-slate-500 text-sm mb-8">
                  Enter the 6-digit code sent to <span className="font-medium text-slate-900">{maskedEmail}</span>
                </p>
                <div className="flex gap-3 justify-center mb-8">
                  {resetCode.map((digit, i) => (
                    <input
                      key={i}
                      ref={el => codeRefs.current[i] = el}
                      value={digit}
                      onChange={e => handleCodeInput(i, e.target.value)}
                      onKeyDown={e => handleCodeKeyDown(i, e)}
                      maxLength={1}
                      className="w-14 h-16 text-3xl font-light text-center border-b-2 border-x-0 border-t-0 border-slate-200 focus:border-[#2A5C4A] rounded-none bg-transparent outline-none focus:ring-0 text-slate-900 transition-colors"
                      data-testid={`reset-code-${i}`}
                    />
                  ))}
                </div>
                <div className="space-y-4 mb-8">
                  <div>
                    <Label className="text-xs font-semibold text-slate-500 tracking-[0.05em] uppercase">New Password</Label>
                    <Input
                      type="password"
                      value={newPassword}
                      onChange={e => setNewPassword(e.target.value)}
                      className="mt-2 h-14 rounded-xl bg-[#F9F9F8] border-slate-200 text-slate-900 focus:border-[#2A5C4A] focus:ring-1 focus:ring-[#2A5C4A]"
                      data-testid="new-password-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-500 tracking-[0.05em] uppercase">Confirm Password</Label>
                    <Input
                      type="password"
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      className="mt-2 h-14 rounded-xl bg-[#F9F9F8] border-slate-200 text-slate-900 focus:border-[#2A5C4A] focus:ring-1 focus:ring-[#2A5C4A]"
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button type="button" variant="outline" onClick={closeForgot} className="flex-1 h-12 rounded-xl border-slate-200 text-slate-600 hover:bg-slate-50" data-testid="cancel-reset-btn-2">
                    Cancel
                  </Button>
                  <Button type="submit" disabled={loading} className="flex-1 h-12 rounded-xl bg-[#2A5C4A] hover:bg-[#1D4034] text-white" data-testid="reset-password-btn">
                    {loading ? 'Resetting...' : 'Reset Password'}
                  </Button>
                </div>
              </form>
            )}

            {resetStep === 'done' && (
              <div className="text-center">
                <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <KeyRound className="h-8 w-8 text-[#2A5C4A]" />
                </div>
                <h3 className="text-2xl font-semibold text-slate-900 mb-2" style={heading}>Password Reset!</h3>
                <p className="text-slate-500 text-sm mb-8">Your password has been reset successfully. You can now sign in with your new password.</p>
                <Button onClick={closeForgot} className="w-full h-12 rounded-xl bg-[#2A5C4A] hover:bg-[#1D4034] text-white" data-testid="back-to-login-btn">
                  Back to Sign In
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
